/**
 * MutateDriverMath.js - Mathematical functions for mutating driver observation data
 *
 * This module provides functions to apply mathematical transformations to observation data
 * based on scenario parameters like start dates, driver values, and date ranges.
 */

/**
 * Applies a linear multiplicative shift to observation values starting from a break point
 *
 * The function creates a "break" at t_break and then applies a linear
 * multiplicative factor that gradually changes, reaching the target value
 * at the end date (t_end).
 *
 * Mathematical approach (confirmed working):
 * 1. Find the break point t_break in the observation timeline
 * 2. Keep all values before t_break unchanged: f(x_n, t_n) = x_n for t_n < t_break
 * 3. After t_break, apply multiplicative factor L(t): f(x_n, t_n) = x_n * L(t_n)
 * 4. L(t_n) = 1 + ((t_n - t_break) / (t_end - t_break)) * (r - 1)
 * 5. r = target / x_end (ratio that maps x_end to target)
 * 6. At t_end: L(t_end) = r, ensuring f(x_end, t_end) = target
 *
 * @param {number} target - The target value at t_end (replaces driverValue)
 * @param {Date} t_break - The break time where the shift begins (replaces startDate)
 * @param {Date} t_start - The earliest date in the observation range (replaces minDate)
 * @param {Date} t_end - The latest date where target is reached (replaces maxDate)
 * @param {number} x_start - The initial observation value (replaces observationValueStart)
 * @param {number} x_end - The final observation value before mutation (replaces observationValueEnd)
 * @param {Array} observationValues - Array of original observation values
 * @param {Array} observationTimecodes - Array of corresponding dates
 * @returns {Array} Array of mutated observation values
 */
export const mutateRule = (
  target,
  t_break,
  t_start,
  t_end,
  x_start,
  x_end,
  observationValues,
  observationTimecodes,
) => {
  // Validate inputs
  if (
    !observationValues ||
    !observationTimecodes ||
    observationValues.length !== observationTimecodes.length
  ) {
    console.warn('Invalid observation data provided to mutateRule')
    return observationValues || []
  }

  // Convert dates to Date objects if they're not already
  const t_breakObj = t_break instanceof Date ? t_break : new Date(t_break)
  const t_endObj = t_end instanceof Date ? t_end : new Date(t_end)

  // Validate that dates are valid Date objects
  if (isNaN(t_breakObj.getTime()) || isNaN(t_endObj.getTime())) {
    console.warn('Invalid dates provided to mutateRule:', { t_break, t_end })
    return observationValues
  }

  if (t_breakObj >= t_endObj) {
    console.warn('t_break must be before t_end in mutateRule')
    return observationValues
  }

  // Convert dates to timestamps for easier comparison
  const t_breakTimestamp = t_breakObj.getTime()
  const t_endTimestamp = t_endObj.getTime()
  const timeRange = t_endTimestamp - t_breakTimestamp

  // If no time range, return original values
  if (timeRange <= 0) {
    return observationValues
  }

  // Calculate the ratio r = target / x_end
  const r = x_end !== 0 ? target / x_end : 1

  // Apply the mutation rule to each observation value
  const mutatedValues = observationValues.map((x_n, index) => {
    const currentTimecode = observationTimecodes[index]
    const currentDateObj =
      currentTimecode instanceof Date
        ? currentTimecode
        : new Date(currentTimecode)

    // Skip if invalid date
    if (isNaN(currentDateObj.getTime())) {
      console.warn('Invalid timecode at index', index, ':', currentTimecode)
      return x_n
    }

    const t_n = currentDateObj.getTime()

    // If we're before the break time, keep the original value unchanged
    if (t_n < t_breakTimestamp) {
      return x_n
    }

    // If we're at or after the break time, apply the linear scaling factor L(t_n)
    // L(t_n) = 1 + ((t_n - t_break) / (t_end - t_break)) * (r - 1)
    const timeFromBreak = t_n - t_breakTimestamp
    const progressRatio = Math.min(timeFromBreak / timeRange, 1.0)
    const L_t_n = 1 + progressRatio * (r - 1)

    // Apply the multiplicative factor: f(x_n, t_n) = x_n * L(t_n)
    return x_n * L_t_n
  })

  return mutatedValues
}

/**
 * Legacy mutate rule for backward compatibility
 * Simply adds 100 to each value
 *
 * @param {Array} observationValues - Array of numeric observation values
 * @returns {Array} Array of mutated observation values
 */
export const legacyMutateRule = (observationValues) => {
  return observationValues.map((value) => value + 100)
}

/**
 * Helper function to validate date ranges
 *
 * @param {Date|string|number} startDate - Start date to validate
 * @param {Date|string|number} minDate - Minimum allowed date
 * @param {Date|string|number} maxDate - Maximum allowed date
 * @returns {boolean} True if dates are valid
 */
export const validateDateRange = (startDate, minDate, maxDate) => {
  if (!startDate || !minDate || !maxDate) {
    return false
  }

  // Convert to Date objects if needed
  const startDateObj =
    startDate instanceof Date ? startDate : new Date(startDate)
  const minDateObj = minDate instanceof Date ? minDate : new Date(minDate)
  const maxDateObj = maxDate instanceof Date ? maxDate : new Date(maxDate)

  // Check if dates are valid
  if (
    isNaN(startDateObj.getTime()) ||
    isNaN(minDateObj.getTime()) ||
    isNaN(maxDateObj.getTime())
  ) {
    return false
  }

  const start = startDateObj.getTime()
  const min = minDateObj.getTime()
  const max = maxDateObj.getTime()

  return start >= min && start <= max && min < max
}

/**
 * Helper function to interpolate between two values based on time progress
 *
 * @param {number} startValue - Starting value
 * @param {number} endValue - Ending value
 * @param {number} progress - Progress ratio (0 to 1)
 * @returns {number} Interpolated value
 */
export const linearInterpolate = (startValue, endValue, progress) => {
  const clampedProgress = Math.max(0, Math.min(1, progress))
  return startValue + (endValue - startValue) * clampedProgress
}

/*
Python proof-of-concept demonstrating the mathematical correctness:

import numpy as np
import matplotlib.pyplot as plt

def L(t_n, t_break, t_end, r):
    """Linear scaling factor L(t_n)"""
    if t_n < t_break:
        return 1.0
    return 1 + ((t_n - t_break) / (t_end - t_break)) * (r - 1)

def f(x_n, t_n, t_break, t_end, x_end, target):
    """Point-wise transformation f(x_n, t_n)"""
    if t_n < t_break:
        return x_n
    r = target / x_end
    return x_n * L(t_n, t_break, t_end, r)

# Test data
times = np.linspace(0, 10, 11)  # t_start=0, t_end=10
values = np.random.exponential(1, 11) + 1  # Some positive values
t_break = 6
target = 5.0
x_end = values[-1]  # Last original value

print(f"Original x_end: {x_end:.3f}")
print(f"Target: {target}")

# Apply transformation
transformed_values = [f(x_n, t_n, t_break, 10, x_end, target) 
                     for x_n, t_n in zip(values, times)]

print(f"Transformed x_end: {transformed_values[-1]:.3f}")
print(f"Match target: {abs(transformed_values[-1] - target) < 1e-10}")

# Verify continuity at break point
break_idx = int(t_break)
if break_idx < len(values):
    print(f"Value before break: {transformed_values[break_idx-1]:.3f}")
    print(f"Value at break: {transformed_values[break_idx]:.3f}")
    print(f"Continuous at break: {abs(values[break_idx-1] - transformed_values[break_idx-1]) < 1e-10}")

Expected output:
- Original x_end will be some random value
- Transformed x_end will be exactly 5.0 (the target)
- Values before t_break=6 remain unchanged
- Values after t_break are scaled linearly to reach the target
*/
